import { useState } from 'react';

interface NoticeItem {
  id: number;
  title: string;
  date: string;
  category: string;
  content: string;
}

export default function Notice() {
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const notices: NoticeItem[] = [];

  const toggleExpand = (id: number) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <section id="notice" className="py-24 bg-white relative overflow-hidden">
      {/* 배경 장식 */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-orange-50 rounded-full blur-3xl opacity-40"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-orange-100 rounded-full blur-3xl opacity-30"></div>
      
      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">공지사항</h2>
          <div className="w-20 h-1 bg-orange-500 mx-auto mb-4"></div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            PLAY PLACE의 운영 공지 및 안내사항을 전달합니다
          </p>
        </div>
        
        <div className="max-w-4xl mx-auto">
          {/* 상단 이미지 */}
          <div className="relative mb-12">
            <div className="absolute inset-0 bg-gradient-to-br from-orange-400 to-orange-600 rounded-3xl transform -rotate-1"></div>
            <img 
              src="https://public.readdy.ai/ai/img_res/276f1e596768ec0b55d3441ca108fcc3.jpg"
              alt="공지사항"
              className="relative rounded-3xl shadow-2xl w-full h-[300px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-3xl"></div>
            <div className="absolute bottom-8 left-8 text-white">
              <div className="flex items-center mb-2">
                <i className="ri-notification-line text-3xl mr-3"></i>
                <h3 className="text-3xl font-bold">최신 소식</h3>
              </div>
              <p className="text-lg">중요한 공지사항을 확인하세요</p>
            </div>
          </div>
          
          {/* 공지사항 목록 - 현재 없음 */}
          {notices.length === 0 ? (
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-12 text-center">
              <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-notification-off-line text-4xl text-orange-500"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">현재 공지사항이 없습니다</h3>
              <p className="text-gray-600">
                새로운 소식이 있을 때 이곳에서 확인하실 수 있습니다
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {notices.map((notice) => (
                <div 
                  key={notice.id}
                  className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300"
                >
                  <div 
                    className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => toggleExpand(notice.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-3">
                          <span className="px-4 py-1 bg-orange-100 text-orange-600 rounded-full text-sm font-semibold">
                            {notice.category}
                          </span>
                          <span className="ml-4 text-gray-500 text-sm flex items-center">
                            <i className="ri-calendar-line mr-2"></i>
                            {notice.date}
                          </span>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2 flex items-center">
                          <i className="ri-megaphone-line text-orange-500 mr-3"></i>
                          {notice.title}
                        </h3>
                      </div>
                      <div className="ml-4">
                        <div className={`w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center transition-transform ${expandedId === notice.id ? 'rotate-180' : ''}`}>
                          <i className="ri-arrow-down-s-line text-xl text-orange-500"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {expandedId === notice.id && (
                    <div className="px-6 pb-6 pt-2 bg-gradient-to-b from-gray-50 to-white border-t border-gray-100">
                      <p className="text-gray-700 leading-relaxed pl-9">
                        {notice.content}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {/* 하단 안내 */}
          <div className="mt-12 bg-gradient-to-r from-orange-50 to-orange-100 rounded-2xl p-8 text-center">
            <div className="flex items-center justify-center mb-4">
              <i className="ri-information-line text-3xl text-orange-500 mr-3"></i>
              <h4 className="text-xl font-bold text-gray-900">더 많은 정보가 필요하신가요?</h4>
            </div>
            <p className="text-gray-700 mb-6">
              추가 문의사항은 문의하기를 통해 연락주시기 바랍니다.
            </p>
            <button 
              onClick={() => {
                const contactSection = document.getElementById('contact');
                contactSection?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="px-8 py-3 bg-orange-500 text-white rounded-full font-semibold hover:bg-orange-600 transition-colors shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              문의하기 바로가기
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}